import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { agentService } from '@salerio/common/src/api/agentService';
import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import styles from './DebugFloatWindow.module.scss';

interface Position {
  x: number;
  y: number;
}

interface DebugFloatWindowProps {
  children?: React.ReactNode;
}

interface AuthSession {
  access_token?: string;
  expires_in?: number;
  expires_at?: number;
  refresh_token?: string;
  token_type?: string;
  user?: {
    id?: string;
    email?: string;
    name?: string;
  };
}

interface DebugInfo {
  user_id?: string;
  workspace_id?: string;
  token?: string;
  email?: string;
  name?: string;
  expires_at?: string;
}

interface BrandReportParams {
  brand_name: string;
  brand_url: string;
  generate_type: 'new' | 'update';
  pulse_id: string;
  report_date: string;
  file_oss_url: string;
}

const DebugFloatWindow: React.FC<DebugFloatWindowProps> = ({ children }) => {
    const { profile, currentWorkspace } = useAuth();
    const [position, setPosition] = useState<Position>(() => {
        try {
            const saved = localStorage.getItem('debug_window_position');
            return saved ? JSON.parse(saved) : { x: 20, y: 200 };
        } catch (e) {
            return { x: 20, y: 200 };
        }
    });
    const [isDragging, setIsDragging] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [debugInfo, setDebugInfo] = useState<DebugInfo>({});
    const [brandReportParams, setBrandReportParams] = useState<BrandReportParams>({
        brand_name: '',
        brand_url: '',
        generate_type: 'new',
        pulse_id: '',
        report_date: new Date().toISOString().split('T')[0],
        file_oss_url: ''
    });
    const [brandReportLoading, setBrandReportLoading] = useState(false);
    const [brandReportResult, setBrandReportResult] = useState<any>(null);
    const dragRef = useRef<HTMLDivElement>(null);
    const startX = useRef(0);
    const startY = useRef(0);
    const [dragTransform, setDragTransform] = useState<Position>({ x: 0, y: 0 });

    const {showToast} = useToast();

    useEffect(() => {
        localStorage.setItem('debug_window_position', JSON.stringify(position));
    }, [position]);

    useEffect(() => {
        // user_pulse_status API is being sunset. Skip remote pulse_id lookup in debug panel.
    }, [])

    useEffect(() => {
        // 从localStorage的bottom-up-auth-session获取用户信息
        const sessionData = localStorage.getItem('bottom-up-auth-session');

        if (sessionData) {
            try {
                const session: AuthSession = JSON.parse(sessionData);

                setDebugInfo({
                    user_id: session.user?.id || 'N/A',
                    workspace_id: currentWorkspace?.id || 'N/A',
                    token: session.access_token || 'N/A',
                    email: session.user?.email || 'N/A',
                    name: session.user?.name || 'N/A',
                    expires_at: session.expires_at
                        ? new Date(session.expires_at * 1000).toLocaleString()
                        : 'N/A'
                });
            } catch (error) {
                console.error('解析auth session失败:', error);
                setDebugInfo({
                    user_id: 'N/A',
                    workspace_id: currentWorkspace?.id || 'N/A',
                    token: 'N/A',
                    email: 'N/A',
                    name: 'N/A',
                    expires_at: 'N/A'
                });
            }
        } else {
            // 如果没有session数据，使用原来的降级方案
            const userId = localStorage.getItem('userId') || localStorage.getItem('user_id') || 'N/A';
            const token = localStorage.getItem('token') || localStorage.getItem('authToken') || 'N/A';

            setDebugInfo({
                user_id: userId,
                workspace_id: currentWorkspace?.id || 'N/A',
                token: token,
                email: 'N/A',
                name: 'N/A',
                expires_at: 'N/A'
            });
        }
    }, [currentWorkspace?.id]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;

            e.preventDefault();

            const newX = e.clientX - startX.current;
            const newY = e.clientY - startY.current;

            const boundedX = Math.max(0, Math.min(newX, window.innerWidth - 200));
            const boundedY = Math.max(0, Math.min(newY, window.innerHeight - 60));

            // 使用 transform 进行实时移动
            setDragTransform({
                x: boundedX - position.x,
                y: boundedY - position.y
            });
        };

        const handleMouseUp = () => {
            if (!isDragging) return;

            // 结束拖动时更新最终位置
            setPosition({
                x: Math.max(0, Math.min(position.x + dragTransform.x, window.innerWidth - 200)),
                y: Math.max(0, Math.min(position.y + dragTransform.y, window.innerHeight - 60))
            });

            // 重置 transform
            setDragTransform({ x: 0, y: 0 });
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove, { passive: false });
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, position, dragTransform.x, dragTransform.y]);

    const handleMouseDown = (e: React.MouseEvent) => {
        startX.current = e.clientX - position.x;
        startY.current = e.clientY - position.y;
        setIsDragging(true);
        e.preventDefault();
    };

    const copyToClipboard = (text: string, label: string) => {
        const copyFn = async () => {
            try {
                await navigator.clipboard.writeText(text);
            } catch (err) {
                console.error('复制失败:', err);
                // 降级方案：使用传统的复制方法
                const textArea = document.createElement('textarea');
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
        };
        copyFn();
        showToast('复制成功', 'success');
    };

    const handleBrandReport = async () => {
        setBrandReportLoading(true);
        setBrandReportResult(null);

        try {
            const result = await agentService.createBrandReport(brandReportParams);
            setBrandReportResult(result);
        } catch (error) {
            setBrandReportResult({
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            });
        } finally {
            setBrandReportLoading(false);
        }
    };

    const handleBrandReportParamChange = (field: keyof BrandReportParams, value: string) => {
        setBrandReportParams(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <div
            className={`${styles.floatWindow} ${isExpanded ? styles.expanded : ''} ${isDragging ? styles.dragging : ''}`}
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                transform: isDragging ? `translate(${dragTransform.x}px, ${dragTransform.y}px)` : 'none',
                cursor: isDragging ? 'grabbing' : 'grab',
                willChange: isDragging ? 'transform' : 'auto'
            }}
        >
            <div
                className={styles.header}
                onMouseDown={handleMouseDown}
            >
                <div className={styles.title}>
                    🐛 Debug Info
                </div>
                <button
                    className={styles.toggleBtn}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    {isExpanded ? '−' : '+'}
                </button>
            </div>

            {isExpanded ? (
                <div className={styles.content}>
                    <div className={styles.section}>
                        <h4>基础信息</h4>
                        <div className={styles.infoList}>
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>User ID:</span>
                                <span className={styles.infoValue}>
                                    {debugInfo.user_id}
                                </span>
                                <button
                                    className={styles.copyBtn}
                                    onClick={() => copyToClipboard(debugInfo.user_id || '', 'User ID')}
                                    title="复制User ID"
                                >
                                    copy
                                </button>
                            </div>

                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Workspace ID:</span>
                                <span className={styles.infoValue}>
                                    {debugInfo.workspace_id}
                                </span>
                                <button
                                    className={styles.copyBtn}
                                    onClick={() => copyToClipboard(debugInfo.workspace_id || '', 'Workspace ID')}
                                    title="复制Workspace ID"
                                >
                                    copy
                                </button>
                            </div>

                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Email:</span>
                                <span className={styles.infoValue}>
                                    {debugInfo.email}
                                </span>
                                <button
                                    className={styles.copyBtn}
                                    onClick={() => copyToClipboard(debugInfo.email || '', 'Email')}
                                    title="复制Email"
                                >
                                    copy
                                </button>
                            </div>

                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Token:</span>
                                <span className={styles.infoValue}>
                                    {debugInfo.token && debugInfo.token.length > 20
                                        ? `${debugInfo.token.substring(0, 20)}...`
                                        : debugInfo.token || 'N/A'
                                    }
                                </span>
                                <button
                                    className={styles.copyBtn}
                                    onClick={() => copyToClipboard(debugInfo.token || '', 'Token')}
                                    title="复制完整Token"
                                >
                                    copy
                                </button>
                            </div>

                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Expires At:</span>
                                <span className={styles.infoValue}>
                                    {debugInfo.expires_at}
                                </span>
                                <button
                                    className={styles.copyBtn}
                                    onClick={() => copyToClipboard(debugInfo.expires_at || '', 'Expires At')}
                                    title="复制Expires At"
                                >
                                    copy
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.section}>
                        <h4>📊 Brand Report</h4>
                        <div className={styles.brandReportForm}>
                            <div className={styles.formGroup}>
                                <label>Brand Name:</label>
                                <input
                                    type="text"
                                    value={brandReportParams.brand_name}
                                    onChange={(e) => handleBrandReportParamChange('brand_name', e.target.value)}
                                    className={styles.formInput}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Brand URL:</label>
                                <input
                                    type="text"
                                    value={brandReportParams.brand_url}
                                    onChange={(e) => handleBrandReportParamChange('brand_url', e.target.value)}
                                    className={styles.formInput}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Report Date:</label>
                                <input
                                    type="date"
                                    value={brandReportParams.report_date}
                                    onChange={(e) => handleBrandReportParamChange('report_date', e.target.value)}
                                    className={styles.formInput}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Pulse ID:</label>
                                <input
                                    type="text"
                                    value={brandReportParams.pulse_id}
                                    onChange={(e) => handleBrandReportParamChange('pulse_id', e.target.value)}
                                    className={styles.formInput}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>File OSS URL:</label>
                                <input
                                    type="text"
                                    value={brandReportParams.file_oss_url}
                                    onChange={(e) => handleBrandReportParamChange('file_oss_url', e.target.value)}
                                    className={styles.formInput}
                                />
                            </div>
                            <button
                                onClick={handleBrandReport}
                                disabled={brandReportLoading}
                                className={styles.submitBtn}
                            >
                                {brandReportLoading ? '生成中...' : '生成 Brand Report'}
                            </button>

                            {brandReportResult ? (
                                <div className={styles.result}>
                                    <h5>结果:</h5>
                                    <pre className={styles.resultContent}>
                                        {JSON.stringify(brandReportResult, null, 2)}
                                    </pre>
                                    <button
                                        onClick={() => copyToClipboard(JSON.stringify(brandReportResult, null, 2), 'Brand Report Result')}
                                        className={styles.copyBtn}
                                    >
                                        copy
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {children ? (
                        <div className={styles.section}>
                            <h4>自定义信息</h4>
                            <div className={styles.customContent}>
                                {children}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default DebugFloatWindow;
